import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  FileSignature, Download, Loader2, Building2, User, Plus, Trash2, FileText, HardHat,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const emptyForm = {
  azienda_id: '', pdv_id: '',
  ragione_sociale: '', indirizzo: '', cap: '', comune: '', provincia: '',
  datore_lavoro: '', rspp: '', medico_competente: '',
};

export default function DichiarazioneSicurezzaPage() {
  const { user } = useAuth();
  const role = user?.role || '';
  const userAziendaIds = user?.allowed_azienda_ids || [];
  const isAdmin = ['super_admin', 'admin', 'amministratore'].includes(role);
  const isProf = ['consulente_haccp', 'haccp_admin', 'haccp_responsabile', 'studio_tecnico', 'rspp'].includes(role);
  const isAziendaUser = ['azienda', 'gerente'].includes(role);
  const canCreate = isAdmin || isProf || isAziendaUser;

  const [aziende, setAziende] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [dichiarazioni, setDichiarazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, aRes, pRes] = await Promise.all([
        api.get('/sicurezza/dichiarazioni'),
        api.get('/aziende').catch(() => ({ data: [] })),
        api.get('/pdv').catch(() => ({ data: [] })),
      ]);
      setDichiarazioni(dRes.data || []);
      const azItems = Array.isArray(aRes.data) ? aRes.data : (aRes.data?.items || []);
      const pdvItems = Array.isArray(pRes.data) ? pRes.data : (pRes.data?.items || []);
      if (isAziendaUser && userAziendaIds.length > 0) {
        setAziende(azItems.filter(a => userAziendaIds.includes(a.id || a._id)));
        setPdvList(pdvItems.filter(p => userAziendaIds.includes(p.azienda_id)));
      } else {
        setAziende(azItems);
        setPdvList(pdvItems);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => {
    const f = { ...emptyForm };
    if (isAziendaUser && aziende.length === 1) {
      const az = aziende[0];
      f.azienda_id = az.id || az._id;
      f.ragione_sociale = az.denominazione || az.ragione_sociale || '';
      f.indirizzo = az.sede_legale || '';
      f.cap = az.cap || ''; f.comune = az.citta || az.comune || ''; f.provincia = az.provincia || '';
    }
    setForm(f);
    setOpenDialog(true);
  };

  const onSelectAz = (azId) => {
    const a = aziende.find(x => (x.id || x._id) === azId);
    if (!a) return;
    setForm(f => ({
      ...f, azienda_id: azId, pdv_id: '',
      ragione_sociale: a.denominazione || a.ragione_sociale || '',
      indirizzo: a.sede_legale || '',
      cap: a.cap || '', comune: a.citta || a.comune || '', provincia: a.provincia || '',
    }));
  };
  const onSelectPdv = (pid) => {
    const p = pdvList.find(x => (x.id || x._id) === pid);
    if (!p) return;
    setForm(f => ({
      ...f, pdv_id: pid,
      azienda_id: p.azienda_id || f.azienda_id,
      ragione_sociale: p.company_name || p.name || f.ragione_sociale,
      indirizzo: p.address || '', cap: p.cap || '',
      comune: p.city || '', provincia: p.province || p.region || '',
    }));
  };

  const submit = async () => {
    if (!form.ragione_sociale.trim()) { toast.error('Ragione sociale obbligatoria'); return; }
    setSaving(true);
    try {
      const r = await api.post('/sicurezza/dichiarazioni', form);
      toast.success('✅ Dichiarazione creata');
      const dx = await api.get(`/sicurezza/dichiarazioni/${r.data.id}/docx`, { responseType: 'blob' });
      const url = URL.createObjectURL(dx.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Dichiarazione_Sicurezza_${form.ragione_sociale.replace(/\s+/g, '_')}.docx`;
      a.click(); URL.revokeObjectURL(url);
      setOpenDialog(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione');
    } finally { setSaving(false); }
  };

  const downloadDocx = async (d) => {
    try {
      const r = await api.get(`/sicurezza/dichiarazioni/${d.id}/docx`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Dichiarazione_Sicurezza_${d.ragione_sociale.replace(/\s+/g, '_')}.docx`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Errore download'); }
  };
  const deleteD = async (d) => {
    if (!window.confirm(`Eliminare la dichiarazione di "${d.ragione_sociale}"?`)) return;
    try {
      await api.delete(`/sicurezza/dichiarazioni/${d.id}`);
      setDichiarazioni(arr => arr.filter(x => x.id !== d.id));
      toast.success('Eliminata');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const pdvForAz = form.azienda_id ? pdvList.filter(p => p.azienda_id === form.azienda_id) : [];

  return (
    <div className="space-y-6" data-testid="dichiarazione-sicurezza-page">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <HardHat className="w-7 h-7 text-orange-600" /> Dichiarazione Sicurezza Luoghi di Lavoro
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Dichiarazione di conformità al D.Lgs. 81/2008 per ogni azienda/UL.
            {isAziendaUser && ' Puoi gestire solo le dichiarazioni della tua azienda.'}
          </p>
        </div>
        {canCreate && (
          <Button onClick={openNew} className="bg-orange-600 hover:bg-orange-700" data-testid="new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuova Dichiarazione
          </Button>
        )}
      </motion.div>

      {loading ? (
        <p className="text-slate-500">Caricamento...</p>
      ) : dichiarazioni.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="pt-10 pb-10 text-center">
            <HardHat className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 mb-2">Nessuna dichiarazione ancora creata.</p>
            {canCreate && (
              <Button onClick={openNew} variant="outline">
                <Plus className="w-4 h-4 mr-1" /> Crea la prima
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {dichiarazioni.map(d => (
            <Card key={d.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-4">
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <FileText className="w-4 h-4 text-orange-600" />
                      <strong className="text-lg">{d.ragione_sociale}</strong>
                      {d.rspp && <Badge variant="outline" className="text-[10px]">RSPP: {d.rspp}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {[d.indirizzo, d.cap, d.comune, d.provincia ? `(${d.provincia})` : ''].filter(Boolean).join(' ')}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Creata da {d.created_by_name} il {new Date(d.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => downloadDocx(d)}>
                      <Download className="w-3 h-3 mr-1" /> .docx
                    </Button>
                    {d.can_delete && (
                      <Button size="sm" variant="ghost" onClick={() => deleteD(d)}
                        className="text-red-600 hover:bg-red-50">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Dichiarazione Sicurezza Luoghi di Lavoro</DialogTitle>
            <DialogDescription>
              Seleziona azienda e (opzionalmente) la UL, oppure compila i dati manualmente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!isAziendaUser && (
              <div>
                <Label>1. Azienda *</Label>
                <select value={form.azienda_id} onChange={e => onSelectAz(e.target.value)}
                  className="w-full border rounded p-2 text-sm" data-testid="az-select">
                  <option value="">— Scegli azienda —</option>
                  {aziende.map(a => {
                    const id = a.id || a._id;
                    return <option key={id} value={id}>{a.denominazione || a.ragione_sociale}</option>;
                  })}
                </select>
              </div>
            )}
            {pdvForAz.length > 0 && (
              <div>
                <Label>2. Unità Locale (opzionale)</Label>
                <select value={form.pdv_id} onChange={e => onSelectPdv(e.target.value)}
                  className="w-full border rounded p-2 text-sm" data-testid="pdv-select">
                  <option value="">— Sede legale —</option>
                  {pdvForAz.map(p => {
                    const id = p.id || p._id;
                    return <option key={id} value={id}>{p.name} {p.city ? `— ${p.city}` : ''}</option>;
                  })}
                </select>
              </div>
            )}
            <div className="grid grid-cols-1 gap-2 pt-2 border-t">
              <div>
                <Label>Ragione sociale *</Label>
                <Input value={form.ragione_sociale}
                  onChange={e => setForm({ ...form, ragione_sociale: e.target.value })}
                  data-testid="rs-input" />
              </div>
              <div>
                <Label>Indirizzo</Label>
                <Input value={form.indirizzo} onChange={e => setForm({ ...form, indirizzo: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>CAP</Label>
                  <Input value={form.cap} onChange={e => setForm({ ...form, cap: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Comune</Label>
                  <Input value={form.comune} onChange={e => setForm({ ...form, comune: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Provincia</Label>
                <Input value={form.provincia} onChange={e => setForm({ ...form, provincia: e.target.value })} className="max-w-xs" />
              </div>
            </div>
            <div className="border-t pt-2 space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2"><User className="w-4 h-4"/> Figure della sicurezza</h4>
              <div>
                <Label>Datore di lavoro</Label>
                <Input value={form.datore_lavoro}
                  onChange={e => setForm({ ...form, datore_lavoro: e.target.value })}
                  placeholder="Nome e cognome" data-testid="datore-input" />
              </div>
              <div>
                <Label>RSPP (Responsabile Servizio Prevenzione e Protezione)</Label>
                <Input value={form.rspp} onChange={e => setForm({ ...form, rspp: e.target.value })}
                  placeholder="Nome e cognome" data-testid="rspp-input" />
              </div>
              <div>
                <Label>Medico Competente</Label>
                <Input value={form.medico_competente}
                  onChange={e => setForm({ ...form, medico_competente: e.target.value })}
                  placeholder="Nome e cognome" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Annulla</Button>
            <Button onClick={submit} disabled={saving || !form.ragione_sociale.trim()}
              className="bg-orange-600 hover:bg-orange-700" data-testid="submit-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSignature className="w-4 h-4 mr-1" />}
              Crea e scarica .docx
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
